// Defensive by design: malformed data is dropped rather than thrown so a corrupt
// payload never crashes the wizard or PDF; the backend zod schema is the authority.

import { DEFAULT_ELASTIC_WEAR, MAX_ELASTIC_ALIGNER, MAX_ELASTIC_TEETH } from './model.ts';
import type { ElasticPlacement, ElasticScheme, LegacyElastics } from './model';
import { LOWER_FDI, UPPER_FDI, toothLabel } from './toothGeometry.ts';

const KNOWN_FDI = new Set<number>([...UPPER_FDI, ...LOWER_FDI]);

/** Labels for the pre-redesign `type` field, so old persisted events still show
 *  a sensible name once elastics became free-text/name-based. */
const LEGACY_TYPE_LABELS: Record<string, string> = {
    classII: 'Class II',
    classIII: 'Class III',
    vertical: 'Vertical',
    cross: 'Cross',
    triangular: 'Triangular',
    box: 'Box'
};

/** Pre-redesign events carry a `type` key instead of `name`; it is mapped to a
 *  readable label so old data still reads correctly. */
export function parseElasticPlacements(
    raw: unknown,
    fallbackWear: string = DEFAULT_ELASTIC_WEAR
): ElasticPlacement[] {
    if (!Array.isArray(raw)) return [];
    const out: ElasticPlacement[] = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object') continue;
        const rec = item as Record<string, unknown>;
        const teethRaw = rec.teeth;
        if (!Array.isArray(teethRaw)) continue;
        const teeth = teethRaw
            .filter((t): t is number => typeof t === 'number' && KNOWN_FDI.has(t))
            .slice(0, MAX_ELASTIC_TEETH);
        if (teeth.length < 2) continue;
        const id = typeof rec.id === 'string' && rec.id.length > 0 ? rec.id : createPlacementId();
        const legacyName = typeof rec.type === 'string' ? (LEGACY_TYPE_LABELS[rec.type] ?? '') : '';
        const name =
            typeof rec.name === 'string' && rec.name.length > 0
                ? rec.name.slice(0, 200)
                : legacyName;
        const size = typeof rec.size === 'string' ? rec.size.slice(0, 200) : '';
        // Wear moved onto the band; older records store it at the scheme level (passed
        // in as `fallbackWear`) or not at all (falls back to the default).
        const wear =
            typeof rec.wear === 'string' && rec.wear.length > 0
                ? rec.wear.slice(0, 200)
                : fallbackWear;
        const color =
            typeof rec.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(rec.color)
                ? rec.color
                : undefined;
        // Lingual buttons: keep only values that are real teeth in this band.
        const teethSet = new Set(teeth);
        const lingualTeeth = Array.isArray(rec.lingualTeeth)
            ? rec.lingualTeeth.filter((t): t is number => typeof t === 'number' && teethSet.has(t))
            : [];
        out.push({
            id,
            name,
            teeth,
            size,
            wear,
            ...(lingualTeeth.length > 0 ? { lingualTeeth } : {}),
            ...(color ? { color } : {})
        });
    }
    return out;
}

/** The set of FDI teeth whose button is lingual across all given placements. */
export function lingualToothSet(placements: ElasticPlacement[]): Set<number> {
    const set = new Set<number>();
    for (const p of placements) for (const fdi of p.lingualTeeth ?? []) set.add(fdi);
    return set;
}

/** Tooth label suffixed with its button side: "12B" (buccal) or "12L" (lingual). */
export function toothWithSide(placement: ElasticPlacement, fdi: number): string {
    return `${toothLabel(fdi)}${placement.lingualTeeth?.includes(fdi) ? 'L' : 'B'}`;
}

/** Stable-enough unique id for a new placement (browser runtime only). */
export function createPlacementId(): string {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
    return `el-${Math.random().toString(36).slice(2)}`;
}

/** Coerce a persisted aligner bound to a positive integer, else the fallback. */
function toAlignerNumber(value: unknown, fallback: number): number {
    const n =
        typeof value === 'number' ? value : typeof value === 'string' ? parseInt(value, 10) : NaN;
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback;
}

/** A fresh scheme. When `prev` is given, its aligner range starts right after
 *  the previous scheme's end so consecutive spans don't overlap by default. */
export function createElasticScheme(prev?: ElasticScheme): ElasticScheme {
    const from = prev ? prev.to + 1 : 1;
    // Default span covers two aligners (e.g. 1 → 2); subsequent schemes start
    // right after the previous one (3 → 4, …).
    const to = from + 1;
    const scheme: ElasticScheme = { id: createPlacementId(), name: '', from, to, elastics: [] };
    // Pre-fill the name with the aligner range as a real (editable) value.
    return { ...scheme, name: elasticSchemeRangeLabel(scheme) };
}

/** Whether any scheme carries at least one placed elastic. */
export function schemesHaveElastics(schemes: ElasticScheme[]): boolean {
    return schemes.some((s) => s.elastics.length > 0);
}

export function hasLegacyElastics(value: LegacyElastics | undefined): boolean {
    if (!value) return false;
    return Boolean(
        value.leftElastic || value.rightElastic || value.leftElasticSize || value.rightElasticSize
    );
}

/** A whole aligner number from 1 to `MAX_ELASTIC_ALIGNER`; 0 is a cleared field. */
export function isAlignerNumberValid(n: number): boolean {
    return Number.isInteger(n) && n >= 1 && n <= MAX_ELASTIC_ALIGNER;
}

/** `0` is the "not entered yet" sentinel for `from`/`to` (aligners start at 1),
 *  so the editor can show an empty field while the bounds stay plain numbers. */
export function alignerRangeErrorMessage(from: number, to: number): string | undefined {
    if (from < 1 && to < 1) return 'Both aligner numbers are required.';
    if (from < 1) return 'The first aligner number is required.';
    if (to < 1) return 'The last aligner number is required.';
    if (!Number.isInteger(from) || !Number.isInteger(to))
        return 'Aligner numbers must be whole numbers.';
    if (from > MAX_ELASTIC_ALIGNER || to > MAX_ELASTIC_ALIGNER)
        return `Aligner numbers can't be higher than ${MAX_ELASTIC_ALIGNER}.`;
    if (to < from) return 'The last aligner must be greater than or equal to the first.';
    return undefined;
}

/** Whether an aligner range is fully specified - see `alignerRangeErrorMessage`. */
export function isAlignerRangeValid(from: number, to: number): boolean {
    return alignerRangeErrorMessage(from, to) === undefined;
}

/** Why the schemes can't be submitted; last ≥ first is our rule, the backend doesn't check it. */
export function schemesRangeErrorMessage(schemes: ElasticScheme[]): string | undefined {
    if (schemes.every((s) => isAlignerRangeValid(s.from, s.to))) return undefined;
    return `Each schema needs a complete aligner range from 1 to ${MAX_ELASTIC_ALIGNER} (last ≥ first)`;
}

function hasValidBounds(scheme: ElasticScheme): boolean {
    return isAlignerNumberValid(scheme.from) && isAlignerNumberValid(scheme.to);
}

/** Why the backend would reject the schemes, in drafts and on submit alike. */
export function schemesBoundsErrorMessage(schemes: ElasticScheme[]): string | undefined {
    if (schemes.every(hasValidBounds)) return undefined;
    return `Each schema needs whole aligner numbers from 1 to ${MAX_ELASTIC_ALIGNER}`;
}

export function schemesWithValidBounds(schemes: ElasticScheme[]): ElasticScheme[] {
    return schemes.filter(hasValidBounds);
}

/** Aligner-span label from raw bounds, e.g. "Aligners 1-5" or "Aligner 3".
 *  Falls back to a generic label while either bound is still unset (`0`). */
export function alignerRangeLabel(from: number, to: number): string {
    if (from < 1 || to < 1) return 'Aligner range';
    return from === to ? `Aligner ${from}` : `Aligners ${from}-${to}`;
}

/** The aligner-span label for a scheme, e.g. "Aligners 1-5" or "Aligner 3". */
export function elasticSchemeRangeLabel(scheme: ElasticScheme): string {
    return alignerRangeLabel(scheme.from, scheme.to);
}

/** Display name for a scheme: the doctor's label, else its aligner range. The
 *  range is the default so a freshly added scheme reads as "Aligners 1-5". */
export function elasticSchemeName(scheme: ElasticScheme): string {
    return scheme.name.trim() || elasticSchemeRangeLabel(scheme);
}

/** Pre-scheme records carry only a flat `elasticsPlacements` array; it migrates
 *  into a single scheme so old submissions still render and stay editable. */
export function parseElasticSchemes(raw: unknown, legacyPlacements?: unknown): ElasticScheme[] {
    const looksLikeScheme = (item: unknown): boolean =>
        !!item &&
        typeof item === 'object' &&
        Array.isArray((item as Record<string, unknown>).elastics);

    if (Array.isArray(raw) && raw.some(looksLikeScheme)) {
        const out: ElasticScheme[] = [];
        for (const item of raw) {
            if (!item || typeof item !== 'object') continue;
            const rec = item as Record<string, unknown>;
            // Migrate a legacy scheme-level `wear` onto each band that lacks its own.
            const legacyWear =
                typeof rec.wear === 'string' && rec.wear.length > 0
                    ? rec.wear
                    : DEFAULT_ELASTIC_WEAR;
            const elastics = parseElasticPlacements(rec.elastics, legacyWear);
            const from = toAlignerNumber(rec.from, 1);
            const to = Math.max(toAlignerNumber(rec.to, from), from);
            const rawName = typeof rec.name === 'string' ? rec.name.slice(0, 200) : '';
            // Default the name to the aligner range so it loads as a real value, not
            // an empty field, for stored/migrated schemes.
            const name = rawName.trim() ? rawName : alignerRangeLabel(from, to);
            const id =
                typeof rec.id === 'string' && rec.id.length > 0 ? rec.id : createPlacementId();
            out.push({ id, name, from, to, elastics });
        }
        return out;
    }

    // Back-compat: a pre-scheme record only has the flat placement array.
    const flat = parseElasticPlacements(legacyPlacements ?? raw);
    if (flat.length === 0) return [];
    return [
        { id: createPlacementId(), name: alignerRangeLabel(1, 1), from: 1, to: 1, elastics: flat }
    ];
}
